import { TextButton } from '@creditornot/cb-button';
import { colors } from '@creditornot/cb-ingredients';
import { Empty } from '@creditornot/cb-placeholders';
import { Spinner } from '@creditornot/cb-progress';
import React, { useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { typographyCss } from 'common/typography';

import { ImageCoverageChart } from './ImageCoverageChart';
import { ImageCoverageDetails } from './ImageCoverageDetails';
import ImageCoverageGuide from './ImageCoverageGuide';
import ImageCoverageTypeSelect from './ImageCoverageTypeSelect';
import useImageCoverageInsights from '../../hooks/useImageCoverageInsights';
import { InsightEntity } from '../../types';
import SectionHeader from '../SectionHeader';
import { SortOrder } from '../tables/types';

export type ImageCoverageSectionProps = {
  insightEntity: InsightEntity;
};

const Root = styled.div`
  border: 1px solid ${({ theme }) => (theme.cbThemeMode === 'light' ? colors.pepper8 : colors.salt12)};
  border-radius: 8px;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  border-block-end: 1px solid ${({ theme }) => (theme.cbThemeMode === 'light' ? colors.pepper8 : colors.salt12)};
  padding: 16px;
  display: flex;
  justify-content: space-between;
`;

const Details = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SeeAllButton = styled(TextButton)`
  ${typographyCss.Body2};
  font-weight: 500;
`;

const Content = styled.div`
  padding: 24px 16px;
  display: grid;
  grid-template-columns: minmax(0, 6fr) minmax(0, 4fr);
  gap: 48px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 32px;
  }
`;

const DataContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const SelectContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledSpinner = styled(Spinner)`
  margin: 0 auto;
`;

const ImageCoverageSection = ({ insightEntity }: ImageCoverageSectionProps) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const [isDetailsOpened, setIsDetailsOpened] = useState(false);
  const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.ASC);
  const intl = useIntl();

  const { imageCoverageRecords, isLoading } = useImageCoverageInsights({
    entityType: insightEntity.type,
    entityId: insightEntity.id,
    sortOrder,
    sortBy: 'COVERAGE',
  });

  return (
    <>
      <Root ref={rootRef}>
        <Header>
          <Details>
            <SectionHeader
              title={intl.formatMessage({ id: 'insights.menu.image-coverage.section.title' })}
              tooltip={intl.formatMessage({ id: 'insights.menu.image-coverage.section.title-tooltip' })}
            />
          </Details>

          <SeeAllButton onClick={() => setIsDetailsOpened(true)}>
            <FormattedMessage id="insights.menu.image-coverage.section.see-all" />
          </SeeAllButton>
        </Header>

        <Content>
          <DataContainer>
            {isLoading ? (
              <StyledSpinner size="large" />
            ) : imageCoverageRecords.length > 0 ? (
              <>
                <SelectContainer>
                  {insightEntity.type === 'venue' ? (
                    <FormattedMessage id="insights.menu.image-coverage.section.type-select.description.category" />
                  ) : (
                    <FormattedMessage id="insights.menu.image-coverage.section.type-select.description.venue" />
                  )}
                  <ImageCoverageTypeSelect value={sortOrder} onChange={setSortOrder} />
                </SelectContainer>
                <ImageCoverageChart imageCoverageRecords={imageCoverageRecords.slice(0, 3)} />
              </>
            ) : (
              <Empty title={intl.formatMessage({ id: 'insights.no-records' })} />
            )}
          </DataContainer>

          <ImageCoverageGuide />
        </Content>
      </Root>
      <ImageCoverageDetails
        insightEntity={insightEntity}
        isOpened={isDetailsOpened}
        onClose={() => setIsDetailsOpened(false)}
      />
    </>
  );
};

export default ImageCoverageSection;
